import ArrowBackRoundedIcon from '@material-ui/icons/ArrowBackRounded';
import ArrowForwardRoundedIcon from '@material-ui/icons/ArrowForwardRounded';
import { Link } from '@reach/router';
import React, { useContext } from 'react';
import styled, { css } from 'styled-components/macro';
import { ThemeContext } from '../../../contexts/Theme';
import {
  focusRing,
  fontSize,
  fontWeight,
  layout,
  lineHeight,
  media,
  neutral,
  radius,
  shadow,
  space,
  transition,
} from '../../../theme/tokens';

export const ResourceContainer = styled.section`
  display: grid;
  grid-template-rows: auto auto;
  align-content: start;
  grid-template-columns: minmax(0, 1fr);
  grid-template-areas: 'resource' 'nav';
  min-height: calc(100vh - ${layout.mobileNavHeight});

  ${media.md`
    min-height: 100vh;
  `}
`;

export const ResourceColumn = styled.div`
  grid-area: resource;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${space[3]};
  width: 100%;
  max-width: ${layout.contentMaxWidth};
  margin: 0 auto;
  padding: ${space[5]} ${layout.gutter} ${space[3]};

  ${media.md`
    gap: ${space[4]};
    padding: ${space[6]} ${layout.gutterWide} ${space[4]};
  `}
`;

export const ModuleHeader = styled.h1`
  margin: 0;
  font-weight: ${fontWeight.heavy};
  font-size: ${fontSize.xxl};
  line-height: ${lineHeight.tight};
  text-align: center;
  overflow-wrap: anywhere;
  color: ${({ theme }) => theme.headerColour};

  ${media.md`
    font-size: ${fontSize.display};
  `}
`;

export const Instructions = styled.p`
  max-width: ${layout.readableMaxWidth};
  margin: 0;
  font-weight: ${fontWeight.medium};
  font-size: ${fontSize.lg};
  line-height: ${lineHeight.normal};
  text-align: center;
  overflow-wrap: anywhere;
  color: ${({ theme }) => theme.headerColour};

  ${media.md`
    font-size: ${fontSize.xl};
  `}
`;

export const EmbedFrame = styled.iframe`
  width: 100%;
  /* sized to leave the back / complete / next row on screen below it */
  height: 55vh;
  min-height: 28rem;
  margin-top: ${space[2]};
  border: 1px solid ${neutral.border};
  border-radius: ${radius.lg};
  background: ${neutral.subtle};
  box-shadow: ${shadow.md};

  ${media.md`
    height: 60vh;
  `}
`;

export const NavRow = styled.nav`
  grid-area: nav;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${space[3]};
  width: 100%;
  max-width: ${layout.readableMaxWidth};
  margin: 0 auto;
  padding: ${space[3]} ${layout.gutter} ${space[5]};
`;

export const NavLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: ${layout.touchTarget};
  min-height: ${layout.touchTarget};
  padding: ${space[1]} ${space[4]};
  font-weight: ${fontWeight.bold};
  font-size: ${fontSize.lg};
  line-height: ${lineHeight.tight};
  color: ${({ theme }) => theme.headerColour};
  background: ${({ theme }) => theme.lessonHighlight};
  border-radius: ${radius.pill};
  box-shadow: ${shadow.sm};
  text-align: center;
  text-decoration: none;
  transition: box-shadow ${transition.fast}, filter ${transition.fast};

  &:hover {
    box-shadow: ${shadow.md};
    filter: brightness(0.95);
  }
  ${focusRing}
  ${({ $lightText }) =>
    $lightText &&
    css`
      color: ${({ theme }) => theme.textLight};
    `}
`;

export const BorderlessButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: ${layout.touchTarget};
  min-height: ${layout.touchTarget};
  border: none;
  border-radius: ${radius.md};
  background: none;
  padding: 0;
  &:hover {
    cursor: pointer;
  }
  ${focusRing}
`;

export const BackArrowIcon = (props) => {
  const theme = useContext(ThemeContext);
  return (
    <ArrowBackRoundedIcon
      style={{ fontSize: '3.2rem', color: theme.appBackground }}
      {...props}
    />
  );
};

export const NextArrowIcon = (props) => {
  const theme = useContext(ThemeContext);
  return (
    <ArrowForwardRoundedIcon
      style={{ fontSize: '3.2rem', color: theme.appBackground }}
      {...props}
    />
  );
};
